import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { findIndex, get, map } from 'lodash';
import Linkify, { linkify } from 'react-linkify';

import useBusiness from '../lib/useBusiness';
import Layout from '../components/layout';
import ArticleHero from '../components/article/ArticleHero';
import ArticleBody from '../components/article/ArticleBody';
import TeaserGrid from '../components/article/TeaserGrid';
import NextArticleNav from '../components/article/NextArticleNav';
import Seo from '../components/seo';
import sizeImage from '../lib/sizeImage';
import styles from './Article.module.styl';

// Add Twitter mentions handler to linkify. From
// https://github.com/markdown-it/linkify-it#example-2-add-twitter-mentions-handler
linkify.add('@', {
  validate(text, pos, self) {
    const tail = text.slice(pos);

    if (!self.re.twitter) {
      // eslint-disable-next-line no-param-reassign
      self.re.twitter = new RegExp(
        `^([a-zA-Z0-9_]){1,15}(?!_)(?=$|${self.re.src_ZPCc})`,
      );
    }
    if (self.re.twitter.test(tail)) {
      // Linkifier allows punctuation chars before prefix,
      // but we additionally disable `@` ("@@mention" is invalid)
      if (pos >= 2 && tail[pos - 2] === '@') {
        return false;
      }
      return tail.match(self.re.twitter)[0].length;
    }
    return 0;
  },
  normalize(match) {
    // eslint-disable-next-line no-param-reassign
    match.url = `https://twitter.com/${match.url.replace(/^@/, '')}`;
  },
});

export const pageQuery = graphql`
    query($storyId: String!) {
        story: arcStory(storyId: {eq: $storyId}) {
            storyId
            headlines {
                basic
            }
            subheadlines {
                basic
            }
            credits {
                by {
                    name
                    affiliation
                    description
                }
            }
            content_elements {
                _id
                type
                subtype
                content
                url
                caption
                alignment
                credits {
                    by {
                        name
                    }
                    affiliation {
                        name
                    }
                }
            }
            promo_items {
                basic {
                    url
                    credits {
                        by {
                            name
                        }
                    }
                    caption
                }
            }
        }
        site {
            siteMetadata {
                title
                defaultSocial
                showSponsorship
            }
        }
    }
`;

const Article = ({ data: { site, story }, pageContext: { storyId, recircData }, path }) => {
  const thisArticleIndex = findIndex(recircData, { storyId });

  // Use modulo arithmetic to wrap around. The idea, if there are 12 articles, 12 % 12 = 0, which
  // will wrap around to the first article (since it's a 0-indexed array).
  const nextArticle = recircData[(thisArticleIndex + 1) % recircData.length];
  useBusiness({
    pageName: `${site.siteMetadata.title}: ${story.headlines.basic}`,
    environment: process.env.NODE_ENV,
    meterContent: (process.env.NODE_ENV !== 'development'),
    pageId: `apps.${story.storyId}`,
    section: 'ideas',
    author: get(story, 'credits.by[0].name', ''),
  });

  const authorDescriptions = map(story.credits.by, 'description').join(' ');

  return (
    <Layout
      path={path}
      pageTitle={`${site.siteMetadata.title}: ${story.headlines.basic}`}
    >
      <Seo
        title={`${site.siteMetadata.title}: ${story.headlines.basic}`}
        description={site.siteMetadata.defaultSocial}
        meta={[
          {
            property: 'og:image',
            content: sizeImage({
              width: 1200,
              height: 630,
              // fitIn: true,
              src: story.promo_items.basic.url,
            }),
          },
        ]}
      />
      <ArticleHero
        site={site}
        story={story}
      />
      <ArticleBody
        contentElements={story.content_elements}
        showSponsorship={site.siteMetadata.showSponsorship}
      />
      {authorDescriptions.trim().length > 0 && (
        <Linkify>
          <aside
            className={styles.authorTag}
          >
            {authorDescriptions}
          </aside>
        </Linkify>
      )}
      {site.siteMetadata.showSponsorship && (
        <aside className={styles.advertiserDisclosure}>
          The Future of Food was produced by Globe Opinion. The advertiser had no role in the
          editorial content.
        </aside>
      )}
      <NextArticleNav headline={nextArticle.headlines.web} to={nextArticle.path} />
      <TeaserGrid currentId={storyId} stories={recircData} />
    </Layout>
  );
};

Article.propTypes = {
  data: PropTypes.shape({
    story: PropTypes.shape({
      storyId: PropTypes.string.isRequired,
      headlines: PropTypes.objectOf(PropTypes.string).isRequired,
      subheadlines: PropTypes.objectOf(PropTypes.string).isRequired,
      credits: PropTypes.shape({
        by: PropTypes.arrayOf(PropTypes.shape({
          name: PropTypes.string.isRequired,
          affiliation: PropTypes.string.isRequired,
        })).isRequired,
      }),
      // eslint-disable-next-line react/forbid-prop-types
      content_elements: PropTypes.array.isRequired,
      overline: PropTypes.string,
      promo_items: PropTypes.shape({
        basic: PropTypes.shape({
          url: PropTypes.string.isRequired,
          credits: PropTypes.shape({
            by: PropTypes.arrayOf(PropTypes.shape({
              name: PropTypes.string.isRequired,
              affiliation: PropTypes.string,
            })).isRequired,
          }),
          caption: PropTypes.string.isRequired,
        }).isRequired,
      }).isRequired,
    }).isRequired,
    site: PropTypes.shape({
      siteMetadata: PropTypes.shape({
        title: PropTypes.string.isRequired,
        defaultSocial: PropTypes.string,
        showSponsorship: PropTypes.bool,
      }).isRequired,
    }).isRequired,
  }).isRequired,
  pageContext: PropTypes.shape({
    storyId: PropTypes.string.isRequired,
    recircData: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      headlines: PropTypes.shape({
        basic: PropTypes.string,
        web: PropTypes.string,
      }),
      promo_items: PropTypes.shape({
        basic: PropTypes.shape({
          url: PropTypes.string,
        }),
      }),
      storyId: PropTypes.string,
      path: PropTypes.string,
    })).isRequired,
  }).isRequired,
  path: PropTypes.string.isRequired,
};

export default Article;
